import * as React from "react";
import { useMemo, useState } from "react";
import { URLS, useAccountSettings } from "@/utils";
import { Combobox, Input, Spinner } from "@/components/ui";
import { FilterType, WebComponents } from "@/interfaces";
import { useHistory } from "react-router-dom";
import { SuperPagesCategoryList } from ".";
import { PageActionProps, PageTitle } from "@/components";
import { debounce } from "lodash";
import { useIsAnySuperPageFetching } from "@/utils/useSuperPageSearch";
import { useNewCategories } from "@/utils/useNewCategories";
import { Category } from "@/services/bedrock";

const SuperPagesList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.VIDEO_PAGES);
  const history = useHistory();

  const statuses: FilterType[] = [
    { text: "Live", key: 0, value: "ACTIVE" },
    { text: "Draft", key: 1, value: "DRAFT" },
  ];
  const [selectedStatus, setSelectedStatus] = useState<FilterType | null>(null);

  const { categories, isFetching } = useNewCategories("SUPER_PAGE");
  const isFetchingPages = useIsAnySuperPageFetching();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const displayedCategories: (Category | null)[] | Category[] = useMemo(() => {
    if (selectedCategories.length === 0) {
      return [...categories, null];
    } else return selectedCategories;
  }, [categories, selectedCategories]);

  const delayedQuery = React.useRef(debounce(setSearchTerm, 500)).current;
  const onChangeSearchTerm = (e: any) => {
    delayedQuery(e.target.value);
  };

  const actions: PageActionProps[] = [];
  if (withWritePermission) {
    actions.push({
      text: "New Super Page",
      onClick: () => history.push(URLS.coach.newSuperPage),
    });
  }
  return (
    <div className="flex flex-col gap-6">
      <PageTitle
        title="Super Pages"
        breadcrumbs={[{ name: "Library", link: URLS.coach.videoPages }]}
        actions={actions}
      />
      <div className="flex gap-4 items-center">
        <Input className="min-w-50 max-w-80" placeholder="Search..." onChange={onChangeSearchTerm} />
        <Combobox
          className="min-w-50 max-w-80"
          searchable
          placeholder="Filter by Category"
          items={categories}
          value={selectedCategories}
          itemToStringLabel={item => item.name}
          onValueChange={value => setSelectedCategories(value)}
          multiple
          maxChips="auto"
        />
        <Combobox
          className="min-w-50 max-w-80"
          searchable
          placeholder="Filter by Status"
          items={statuses}
          value={selectedStatus}
          itemToStringLabel={item => item.text}
          onValueChange={value => setSelectedStatus(value)}
          showClear={true}
        />
        {(isFetching || isFetchingPages) && <Spinner />}
      </div>
      {displayedCategories.length > 0 && (
        <div className="flex flex-col gap-8">
          {displayedCategories.map(category => (
            <SuperPagesCategoryList
              key={`super-page-category-${category?.id ?? "uncategorized"}`}
              searchTerm={searchTerm}
              category={category}
              selectedStatus={selectedStatus?.value}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { SuperPagesList };
